import { useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAppDispatch, useAppSelector, useOutsideClick } from '../hooks'
import {
  deleteTemplate,
  fetchTemplates,
  setPayload,
  setTemplate,
  setThumbnails
} from '../store/form.slice'
import { getObject } from '../services/aws.service'
import { Spinner } from '../components/spinner'
import { Status } from '../components/status'
import { capitalize, isFormDirty, isMobile, searchAndFilterForms } from '../utils/functions'
import { TemplateSchema } from '../utils/types/form'
import SVG from '../assets/undraw_the-void_i26b.svg'

// TODO fix navigation on query filters

const adminEmail = process.env.REACT_APP_ADMIN_EMAIL

export const TemplatesView = () => {
  const [view, setView] = useState<'grid' | 'list'>()
  const [templates, setTemplates] = useState<TemplateSchema[]>([])
  const [selectedTemplate, setSelectedTemplate] = useState<TemplateSchema | null>(null)
  const [searchValue, setSearchValue] = useState('')
  const [filterBy, setFilterBy] = useState('')
  const [isFilterOptionsOpen, setIsFilterOptionsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [requestState, setRequestState] = useState({ info: '', error: '' })
  const searchRef = useRef<HTMLInputElement>(null)
  const templatesListRef = useRef<(HTMLLIElement | null)[]>([])
  const filterByTriggerRef = useRef(null)
  const filterByContextRef = useRef(null)
  const selectedTemplateTriggerRef = useRef(null)
  const selectedTemplateContextRef = useRef(null)

  useOutsideClick(
    {
      element: filterByContextRef,
      trigger: filterByTriggerRef
    },
    () => setIsFilterOptionsOpen(false)
  )

  useOutsideClick(
    {
      element: selectedTemplateContextRef,
      trigger: selectedTemplateTriggerRef
    },
    () => setSelectedTemplate(null)
  )

  const dispatch = useAppDispatch()
  const authState = useAppSelector((state) => state.auth)
  const { data: authUser } = authState
  const formState = useAppSelector((state) => state.form)
  const { templates: globalTemplates, thumbnails } = formState
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()

  const filterQuery = searchParams.get('filter')
  const isDev = authUser?.email === adminEmail

  useEffect(() => {
    if (!globalTemplates.length) {
      initTemplates()
    }

    const viewPref = localStorage.getItem('view') as 'grid' | 'list' | null
    setView(viewPref ? viewPref : isMobile() ? 'list' : 'grid')

    // eslint-disable-next-line
  }, [])

  useEffect(() => {
    filterQuery ? handleFilter(filterQuery, searchValue) : handleFilter()
    populateThumbnails(globalTemplates)

    // eslint-disable-next-line
  }, [globalTemplates, filterQuery])

  const initTemplates = async () => {
    try {
      setIsLoading(true)
      await dispatch(fetchTemplates()).unwrap()
    } catch (error: any) {
      setRequestState({ ...requestState, error: error.message })
    } finally {
      setIsLoading(false)
    }
  }

  const populateThumbnails = async (templates: TemplateSchema[]) => {
    const unresolvedTemplateIds: string[] = []

    templates.forEach(({ _id }) => {
      if (!Object.keys(thumbnails).includes(_id)) {
        unresolvedTemplateIds.push(_id)
      }
    })

    if (unresolvedTemplateIds.length) {
      const allRequests = []

      try {
        const newThumbnails: Record<string, string> = {}

        for (const templateId of unresolvedTemplateIds) {
          allRequests.push(getObject(templateId))
        }

        const allUrls = await Promise.all(allRequests)

        for (let i = 0; i < unresolvedTemplateIds.length; i++) {
          for (let j = 0; j < allUrls.length; j++) {
            const url = allUrls[j]

            if (url && i === j) {
              newThumbnails[unresolvedTemplateIds[i]] = url
            }
          }
        }

        dispatch(setThumbnails(newThumbnails))
      } catch (error: any) {
        setRequestState({ ...requestState, error: error.message })
      }
    }
  }

  const handleViewList = (view: 'grid' | 'list') => {
    setView(view)
    localStorage.setItem('view', view)
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value.toLowerCase()
    setSearchValue(searchValue)
    handleFilter(filterBy, searchValue)
  }

  const handleFilter = (filterBy: string = '', searchBy: string = searchValue) => {
    const filteredTemplates = searchAndFilterForms({
      items: globalTemplates,
      searchBy,
      filterBy
    })

    setTemplates(filteredTemplates)
    setFilterBy(filterBy)
  }

  const handleSelectTemplate = async (
    e: React.MouseEvent<HTMLSpanElement>,
    template: TemplateSchema
  ) => {
    e.stopPropagation()

    selectedTemplate
      ? selectedTemplate._id === template._id
        ? setSelectedTemplate(null)
        : setSelectedTemplate(template)
      : setSelectedTemplate(template)
  }

  const handlePreviewTemplate = (template: TemplateSchema) => {
    dispatch(setTemplate(template))
    navigate(`/form/new/preview?action=__previewTemplate`, {
      viewTransition: true
    })
  }

  const handleEditTemplate = (e: React.MouseEvent<HTMLSpanElement, MouseEvent>) => {
    e.stopPropagation()

    if (isFormDirty()) {
      setRequestState({
        ...requestState,
        info: 'Cannot edit template while a new form is being drafted.'
      })
      setSelectedTemplate(null)
    } else if (selectedTemplate) {
      dispatch(setPayload({ form: selectedTemplate }))
      navigate(`/form/${selectedTemplate._id}/edit?action=__editTemplate`, {
        viewTransition: true
      })
    }
  }

  const handleDeleteTemplate = async (
    e: React.MouseEvent<HTMLSpanElement>,
    template: TemplateSchema,
    index: number
  ) => {
    e.stopPropagation()

    if (window.confirm(`Confirm delete: ${template.name}`)) {
      requestState.error && setRequestState({ ...requestState, error: '' })

      if (selectedTemplate) {
        try {
          templatesListRef.current[index]?.classList.add('disabled', 'disabled-no-fade')
          const templateId = selectedTemplate._id
          setSelectedTemplate(null)

          await dispatch(deleteTemplate(templateId)).unwrap()
        } catch (error: any) {
          setRequestState({ ...requestState, error: error.message })
          templatesListRef.current[index]?.classList.remove('disabled', 'disabled-no-fade')
        }
      }
    }
  }

  return (
    <>
      <div className='main_content dashboard_content'>
        {requestState.info && (
          <Status
            info={requestState.info}
            reset={() => setRequestState({ ...requestState, info: '' })}
          />
        )}
        {requestState.error && (
          <Status
            error={requestState.error}
            reset={() => setRequestState({ ...requestState, error: '' })}
          />
        )}
        <h1 className='heading-styled'>Templates</h1>
        <div className='input_container-subtext'>
          <div className='input_container input_container-reverse'>
            <span className='material-symbols-rounded' onClick={() => searchRef.current?.focus()}>
              search
            </span>
            <input
              type='search'
              value={searchValue}
              onChange={handleSearch}
              placeholder='Search anything...'
              ref={searchRef}
            />
          </div>
          {!!templates.length && (
            <span className='subtext subtext-thin'>
              {`${searchValue || filterBy ? `${templates.length} match${templates.length === 1 ? '' : 'es'} found` : 'Showing all templates'}`}
            </span>
          )}
        </div>
        <div className='view_as'>
          <span
            className={`material-symbols-rounded material-symbols-sm ${view === 'grid' ? 'material-symbols-selected' : ''}`}
            onClick={() => handleViewList('grid')}>
            grid_view
          </span>
          <span
            className={`material-symbols-rounded material-symbols-sm ${view === 'list' ? 'material-symbols-selected' : ''}`}
            onClick={() => handleViewList('list')}>
            table_rows
          </span>
          <div className='actions_container'>
            <div className='search_container'>
              <div className='actions-select'>
                <div className='actions_series'>
                  <div>
                    <div
                      id='filters_trigger'
                      className='div-with-icon'
                      onClick={() => setIsFilterOptionsOpen(!isFilterOptionsOpen)}
                      ref={filterByTriggerRef}>
                      <span id='filters_trigger'>Showing: {capitalize(filterBy) || 'All'}</span>
                      <span id='filters_trigger' className='material-symbols-rounded'>
                        {isFilterOptionsOpen ? 'arrow_drop_down' : 'arrow_right'}
                      </span>
                    </div>
                    {isFilterOptionsOpen && (
                      <div
                        className='context_menu context_menu-down context_menu-wrap'
                        id='filtersContext'
                        ref={filterByContextRef}>
                        <div className='context_menu-grid context-header'>
                          <input
                            id='filter_all'
                            type='checkbox'
                            checked={!filterBy}
                            onChange={() => {
                              handleFilter()
                              setSearchParams((searchParams) => {
                                searchParams.has('filter') && searchParams.delete('filter')
                                return searchParams
                              })
                            }}
                          />
                          <label htmlFor='filter_all' className='highlight-styled-hover'>
                            All
                          </label>
                          <input
                            id='filter_private'
                            type='checkbox'
                            checked={filterBy === 'private'}
                            onChange={() => {
                              handleFilter('private')
                              setSearchParams((searchParams) => {
                                searchParams.set('filter', 'private')
                                return searchParams
                              })
                            }}
                          />
                          <label htmlFor='filter_private' className='highlight-styled-hover'>
                            Private
                          </label>
                          <input
                            id='filter_public'
                            type='checkbox'
                            checked={filterBy === 'public'}
                            onChange={() => {
                              handleFilter('public')
                              setSearchParams((searchParams) => {
                                searchParams.set('filter', 'public')
                                return searchParams
                              })
                            }}
                          />
                          <label htmlFor='filter_public' className='highlight-styled-hover'>
                            Public
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        {isLoading ? (
          <span>...</span>
        ) : (
          <div>
            {templates.length ? (
              <ul className={`content ${view === 'grid' ? 'content-grid' : 'content-list'}`}>
                {templates.map((template, index) => (
                  <li
                    className={`theme-content-${template.meta.colour}`}
                    onClick={() => handlePreviewTemplate(template)}
                    key={template._id}
                    ref={(el) => {
                      templatesListRef.current[index] = el
                    }}>
                    <div className='card_container'>
                      {!thumbnails[template._id] ? (
                        <Spinner />
                      ) : (
                        <>
                          <img src={thumbnails[template._id]} alt={template.name} />
                          {view === 'grid' && (
                            <div className='img-actions'>
                              <span
                                className={
                                  'badge ' + (template.isPublic ? 'badge-green' : 'badge-blue')
                                }>
                                {template.isPublic ? 'Public' : 'Private'}
                              </span>
                            </div>
                          )}
                        </>
                      )}
                    </div>
                    <div className='content-item-title'>
                      <div>
                        <h3>{template.name}</h3>
                        {view === 'list' && (
                          <span>
                            <em
                              className={
                                template.isPublic ? 'badge-green-icon' : 'badge-blue-icon'
                              }>
                              {' ● '}
                            </em>
                            <em
                              className={
                                'badge ' + (template.isPublic ? 'badge-green' : 'badge-blue')
                              }>
                              {template.isPublic ? 'Public' : 'Private'}
                            </em>
                          </span>
                        )}
                      </div>
                      <div className='content-item-details'>
                        <span>{template.description || 'Description: --'}</span>
                      </div>
                    </div>
                    {isDev && (
                      <div className='options'>
                        <span
                          id='options_trigger'
                          className='material-symbols-rounded options_trigger'
                          onClick={(e) => handleSelectTemplate(e, template)}
                          ref={selectedTemplateTriggerRef}>
                          more_vert
                        </span>
                        {selectedTemplate?._id === template._id && (
                          <div
                            className={`context_menu ${view === 'list' ? 'context_menu-right' : ''} context_menu-wrap`}
                            ref={selectedTemplateContextRef}>
                            <div
                              className='div-with-icon highlight-styled-hover'
                              onClick={handleEditTemplate}>
                              <span className='material-symbols-rounded'>edit</span>
                              <span>Edit template</span>
                            </div>
                            <div
                              className='div-with-icon error'
                              onClick={(e) => handleDeleteTemplate(e, template, index)}>
                              <span className='material-symbols-rounded'>delete</span>
                              <span>Delete template</span>
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <div className='svg_container'>
                <img src={SVG} alt='No results' />
                <span className='no_results'>No results.</span>
              </div>
            )}
          </div>
        )}
      </div>
      {isDev && (
        <button
          className='button-with-icon cta'
          onClick={() => {
            navigate(`/form/new/edit?action=__addTemplate`, {
              viewTransition: true
            })
          }}>
          <span className='material-symbols-rounded material-symbols-sm'>add</span>
          <span>Create template</span>
        </button>
      )}
      <div className='footer_actions footer-sticky theme-content-default'>
        <span
          className='material-symbols-rounded badge-rounded'
          onClick={() => navigate(`/form/new/edit`, { viewTransition: true })}>
          add
        </span>
      </div>
    </>
  )
}
